/**
 * Theme Context
 *
 * Provides global theme state that can be accessed and updated
 * from any component in the app. Follows AuthContext.tsx pattern.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { Theme, ThemeId, ThemeContextType } from '../theme/types';
import { getTheme } from '../theme/themes';
import { getStoredTheme, storeTheme } from '../services/themeStorage';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Default theme to use during initialization
 */
const DEFAULT_THEME_ID: ThemeId = 'bauhaus';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeId, setThemeIdState] = useState<ThemeId>(DEFAULT_THEME_ID);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load theme on mount
  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = async () => {
    try {
      const storedTheme = await getStoredTheme();
      setThemeIdState(storedTheme);
    } catch (error) {
      console.error('Failed to load theme:', error);
      setThemeIdState(DEFAULT_THEME_ID);
    } finally {
      setIsInitialized(true);
    }
  };

  const setTheme = async (newThemeId: ThemeId) => {
    try {
      // Persist theme selection
      await storeTheme(newThemeId);

      // Update state
      setThemeIdState(newThemeId);
    } catch (error) {
      console.error('Failed to save theme:', error);
      // Still update state even if storage fails
      setThemeIdState(newThemeId);
    }
  };

  const theme: Theme = getTheme(themeId);

  return (
    <ThemeContext.Provider value={{ theme, themeId, setTheme, isInitialized }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * useTheme hook
 *
 * Access theme context in any component.
 * Throws error if used outside ThemeProvider.
 *
 * @example
 * const { theme, themeId, setTheme } = useTheme();
 *
 * const styles = {
 *   container: {
 *     backgroundColor: theme.colors.background,
 *     padding: theme.spacing.lg,
 *   }
 * };
 */
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
